/**
 * Deterministic, offline MLA-9 Works Cited linter (issue #106).
 *
 * Pure string-in → findings-out, mirroring the shape of the APA linter
 * (`src/lib/citation-check.ts`). This module is intentionally SELF-CONTAINED: it does
 * NOT import or touch the citation-formatting core (`src/lib/citation.ts`,
 * `src/lib/bibliography.ts`) or any CSL fixture, so it can never regress a golden
 * snapshot. Every finding is *advisory* — a "likely issue to review", never a claim that
 * a reference is correct. The checks are deliberately high-precision (a false positive is
 * worse than a miss).
 *
 * IMPORTANT: MLA 9 conventions are partly the OPPOSITE of APA 7 — authors keep their full
 * given name (not initials), multiple authors are joined with the word "and" (not "&"),
 * and titles use Title Case (not sentence case). Reusing the APA checker on MLA text would
 * therefore give actively wrong advice, which is why this is a separate module.
 */

export type Confidence = "high" | "medium";

export interface Finding {
  /** Stable rule id (used as React key / for tests). */
  rule: string;
  /** Short human-readable label. */
  title: string;
  /** Explanation of what looks off and why. */
  message: string;
  /** Optional copy-to-clipboard suggested fix. */
  suggestion?: string;
  confidence: Confidence;
  /** "Why" link — an authoritative MLA guideline or one of our own tools. */
  learnMoreUrl: string;
  learnMoreLabel: string;
}

export interface EntryReport {
  /** 1-based position in the pasted list. */
  position: number;
  /** The normalized single-line entry text that was checked. */
  text: string;
  findings: Finding[];
}

export interface CheckReport {
  entries: EntryReport[];
  /** List-level findings (ordering + duplicates) that span multiple entries. */
  listFindings: Finding[];
  totalEntries: number;
  totalFindings: number;
}

// --- Module-level regexes (Biome: regex must not live inside loops) ------------

const RE_BLANK_LINE = /\n[ \t]*\n/;
const RE_BLANK_LINE_SPLIT = /\n[ \t]*\n/;
const RE_NEWLINE = /\n/;
const RE_SENTENCE_BREAK = /\.\s/;
const RE_LEADING_ARTICLE = /^(?:a|an|the)\s+/i;
const RE_QUOTED_TITLE = /["“”]([^"“”]+)["“”]/;
const RE_BAD_DOI = /(dx\.doi\.org|http:\/\/(?:dx\.)?doi\.org)/i;
const RE_DX_OR_HTTP_DOI = /https?:\/\/(?:dx\.)?doi\.org\/(\S+?)[).]?$/i;
const RE_ENDS_WITH_URL = /(https?:\/\/\S+|\b10\.\d{4,}\/\S+)$/;
const RE_TERMINAL_PUNCT = /[.?!]$/;
const RE_AMPERSAND = /\s&\s|,?\s&\s/;
const RE_AMPERSAND_REPLACE = /,?\s&\s/;
const RE_FIRST_LAST = /^[A-Z][a-z]+(?:\s+[A-Z][a-z]*\.?)*\s+[A-Z][a-z]+\b/;
const RE_TWO_WORD_NAME = /^([A-Z][a-z]+)\s+([A-Z][a-z]+)$/;
const RE_TOKEN_SPLIT = /\s+/;
const RE_TOKEN_PUNCT = /[^a-z]/g;
const RE_NON_LETTER = /[^a-z]/g;
const RE_NON_LETTER_KEEP_CASE = /[^a-zA-Z]/g;
const RE_WORD_SPLIT = /\s+/;
const RE_PUNCT_STRIP = /[^a-z0-9]+/g;
const RE_LEADING_LOWER = /^[a-z]/;
const RE_TRAILING_COMMA = /,$/;
const RE_PIPES = /\|/g;
const RE_YEAR_ANYWHERE = /\b(\d{4})\b/;

/**
 * Words MLA leaves lowercase inside a title (articles, coordinating conjunctions, and
 * prepositions of any length) — so a correct MLA Title-Case title can legitimately contain
 * lowercase words like "through" or "between". The Title-Case heuristic ignores these so it
 * only judges genuine *principal* words.
 */
const MLA_LOWERCASE_WORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "but",
  "for",
  "nor",
  "or",
  "so",
  "yet",
  "as",
  "at",
  "by",
  "in",
  "of",
  "off",
  "on",
  "per",
  "to",
  "up",
  "via",
  "with",
  "from",
  "into",
  "onto",
  "over",
  "than",
  "upon",
  "near",
  "amid",
  "atop",
  "unto",
  "till",
  "save",
  "plus",
  "among",
  "around",
  "about",
  "above",
  "after",
  "along",
  "below",
  "beside",
  "between",
  "beyond",
  "during",
  "except",
  "inside",
  "toward",
  "under",
  "until",
  "within",
  "without",
  "across",
  "against",
  "before",
  "behind",
  "beneath",
  "outside",
  "through",
  "towards",
  "throughout",
  "regarding",
  "concerning",
  "despite",
  "versus",
  "amongst",
  "besides",
]);

// MLA guideline references reused across findings (official MLA Style Center).
const MLA_QUICK_GUIDE_URL = "https://style.mla.org/works-cited-a-quick-guide/";
const MLA_CORE_ELEMENTS_URL =
  "https://style.mla.org/works-cited/core-elements/";
const MLA_AUTHOR_URL =
  "https://style.mla.org/works-cited/core-elements/author/";
const MLA_TITLES_URL =
  "https://style.mla.org/works-cited/core-elements/title-of-source/";

/**
 * Split pasted text into individual reference entries. Blank-line-separated blocks are
 * treated as one entry each (wrapped/hanging-indent lines are rejoined with a space);
 * otherwise each non-empty line is its own entry.
 */
export function splitEntries(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed) {
    return [];
  }
  if (RE_BLANK_LINE.test(trimmed)) {
    return trimmed
      .split(RE_BLANK_LINE_SPLIT)
      .map((block) =>
        block
          .split(RE_NEWLINE)
          .map((line) => line.trim())
          .join(" ")
          .trim()
      )
      .filter(Boolean);
  }
  return trimmed
    .split(RE_NEWLINE)
    .map((line) => line.trim())
    .filter(Boolean);
}

/**
 * The author segment is everything before the first sentence-ending period (the period
 * that closes the MLA author element). Unlike APA there is no "(year)" delimiter, so we
 * rely on the first ". " boundary.
 */
function authorSegment(entry: string): string | null {
  const idx = entry.search(RE_SENTENCE_BREAK);
  if (idx <= 0) {
    return null;
  }
  return entry.slice(0, idx).trim().replace(RE_TRAILING_COMMA, "");
}

/**
 * The title segment is the quoted source title if present (article/chapter), otherwise the
 * second sentence-delimited element (a standalone book/work title).
 */
function titleSegment(entry: string): string | null {
  const quoted = RE_QUOTED_TITLE.exec(entry);
  if (quoted) {
    return quoted[1].trim();
  }
  const idx = entry.search(RE_SENTENCE_BREAK);
  if (idx < 0) {
    return null;
  }
  const rest = entry.slice(idx + 1).trim();
  const next = rest.search(RE_SENTENCE_BREAK);
  if (next < 0) {
    return rest.replace(RE_TERMINAL_PUNCT, "").trim();
  }
  return rest.slice(0, next).trim();
}

/**
 * Tokens that mark an author segment as an ORGANIZATION / corporate body rather than a
 * personal name. Corporate authors (UN/WHO/government/NGO/university-press reports) are
 * cited in MLA *as-is* — "United Nations." is already correct and must NOT be flagged as a
 * "First Last" personal name. Because a false positive is worse than a miss, any author
 * segment containing one of these institutional or function words is treated as an
 * organization and skipped by the author-inversion rule.
 */
const ORG_INDICATOR_WORDS = new Set([
  "academy",
  "administration",
  "agency",
  "alliance",
  "american",
  "association",
  "authority",
  "board",
  "british",
  "bureau",
  "center",
  "centre",
  "coalition",
  "college",
  "commission",
  "committee",
  "company",
  "congress",
  "consortium",
  "corporation",
  "council",
  "department",
  "european",
  "federal",
  "federation",
  "for",
  "foundation",
  "fund",
  "government",
  "group",
  "institute",
  "institutes",
  "institution",
  "international",
  "laboratory",
  "league",
  "ministry",
  "national",
  "nations",
  "network",
  "office",
  "of",
  "organisation",
  "organization",
  "press",
  "program",
  "programme",
  "republic",
  "royal",
  "service",
  "services",
  "society",
  "states",
  "trust",
  "union",
  "united",
  "university",
  "world",
]);

/**
 * Heuristic: does the author segment look like an organization/corporate body rather than a
 * personal name? True when any whitespace token is an institutional or function word from
 * `ORG_INDICATOR_WORDS` (e.g. "United Nations", "World Health Organization", "Modern
 * Language Association of America"). Deliberately conservative — these words effectively
 * never appear in a personal given/surname, so this trades a rare miss for high precision.
 */
function looksOrganizational(authorSeg: string): boolean {
  return authorSeg
    .split(RE_TOKEN_SPLIT)
    .some((token) =>
      ORG_INDICATOR_WORDS.has(token.toLowerCase().replace(RE_TOKEN_PUNCT, ""))
    );
}

/** Build "Smith, John" (full given name — MLA, NOT APA initials) when unambiguous. */
function invertSimpleName(authorSeg: string): string | null {
  const match = RE_TWO_WORD_NAME.exec(authorSeg);
  if (!match) {
    return null;
  }
  const [, first, last] = match;
  return `${last}, ${first}`;
}

/** Rule 1 — author looks like "First Last" instead of MLA "Last, First". */
function checkAuthorOrder(authorSeg: string): Finding | null {
  if (authorSeg.includes(",")) {
    return null;
  }
  if (!RE_FIRST_LAST.test(authorSeg)) {
    return null;
  }
  // Corporate/organization authors ("United Nations.", "World Health Organization.") are
  // already correct in MLA — never flag them as a misordered personal name.
  if (looksOrganizational(authorSeg)) {
    return null;
  }
  const suggestion = invertSimpleName(authorSeg);
  return {
    rule: "author-name-order",
    title: 'Author may be in "First Last" order',
    message:
      'MLA inverts the first author to "Last name, First name" — keeping the full given name (e.g. "Smith, John"), not initials. This entry appears to lead with a given name. Confirm the author is inverted.',
    suggestion: suggestion ?? undefined,
    confidence: suggestion ? "high" : "medium",
    learnMoreUrl: MLA_AUTHOR_URL,
    learnMoreLabel: "MLA: author element",
  };
}

/** Rule 2 — "&" between authors where MLA uses the word "and" (reverse of the APA rule). */
function checkAndConnector(authorSeg: string): Finding | null {
  if (!RE_AMPERSAND.test(authorSeg)) {
    return null;
  }
  return {
    rule: "ampersand-vs-and",
    title: 'Use "and", not "&", before the final author',
    message:
      'In an MLA Works Cited entry, join the last two authors with the word "and" — e.g. "Smith, John, and Jane Doe". The ampersand ("&") is an APA convention, not MLA.',
    suggestion: authorSeg.replace(RE_AMPERSAND_REPLACE, ", and "),
    confidence: "high",
    learnMoreUrl: MLA_AUTHOR_URL,
    learnMoreLabel: "MLA: author element",
  };
}

/** Significant words = 4+ letters, used for the Title Case heuristic. */
function principalWords(title: string): string[] {
  return title
    .trim()
    .split(RE_WORD_SPLIT)
    .slice(1) // the first word is always capitalized in both title and sentence case
    .filter((w) => w.replace(RE_NON_LETTER_KEEP_CASE, "").length >= 4)
    .filter((w) => !MLA_LOWERCASE_WORDS.has(w.toLowerCase()));
}

/**
 * Rule 3 — a source/article title typed in sentence case where MLA wants Title Case.
 * High-precision: only fires when EVERY principal (4+ letter, non-function) word after the
 * first is lowercase — a real Title-Case title (even one that lowercases prepositions like
 * "through" or "of") keeps its content words capitalized and so will not trip this.
 */
function checkTitleCase(entry: string): Finding | null {
  const title = titleSegment(entry);
  if (!title) {
    return null;
  }
  const words = principalWords(title);
  if (words.length < 3) {
    return null;
  }
  const allLowercase = words.every((w) => RE_LEADING_LOWER.test(w));
  if (!allLowercase) {
    return null;
  }
  return {
    rule: "title-case-smell",
    title: "Title may be in sentence case",
    message:
      'MLA uses Title Case for source titles — capitalize the first word, the last word, and all principal words (nouns, verbs, adjectives), e.g. "The Effects of Sleep on Memory". This title looks like it is in sentence case.',
    confidence: "medium",
    learnMoreUrl: MLA_TITLES_URL,
    learnMoreLabel: "MLA: titles of sources",
  };
}

/** Rule 5 — http:// or dx.doi.org DOI instead of a clean https://doi.org link. */
function checkDoi(entry: string): Finding | null {
  if (!RE_BAD_DOI.test(entry)) {
    return null;
  }
  const dx = RE_DX_OR_HTTP_DOI.exec(entry);
  const doiTail = dx?.[1];
  return {
    rule: "doi-url-format",
    title: "DOI uses an outdated host or insecure scheme",
    message:
      "MLA 9 recommends citing a DOI as a clean https://doi.org/10.xxxx/yyyy link. Avoid the deprecated dx.doi.org host and the insecure http:// scheme.",
    suggestion: doiTail ? `https://doi.org/${doiTail}` : undefined,
    confidence: "high",
    learnMoreUrl: MLA_CORE_ELEMENTS_URL,
    learnMoreLabel: "MLA: location (DOIs & URLs)",
  };
}

/** Rule 4 — entry does not end in a period (and does not end in a URL/DOI). */
function checkTerminalPeriod(entry: string): Finding | null {
  const trimmed = entry.trim();
  if (RE_ENDS_WITH_URL.test(trimmed) || RE_TERMINAL_PUNCT.test(trimmed)) {
    return null;
  }
  return {
    rule: "terminal-period",
    title: "Entry does not end with a period",
    message:
      "Every MLA Works Cited entry ends with a period. This entry ends without terminal punctuation.",
    confidence: "high",
    learnMoreUrl: MLA_QUICK_GUIDE_URL,
    learnMoreLabel: "MLA: Works Cited quick guide",
  };
}

/** Run all per-entry rules for a single normalized entry. */
function checkEntry(entry: string): Finding[] {
  const findings: Finding[] = [];
  const authorSeg = authorSegment(entry);
  if (authorSeg) {
    const order = checkAuthorOrder(authorSeg);
    if (order) {
      findings.push(order);
    }
    const connector = checkAndConnector(authorSeg);
    if (connector) {
      findings.push(connector);
    }
  }
  const titleCase = checkTitleCase(entry);
  if (titleCase) {
    findings.push(titleCase);
  }
  const period = checkTerminalPeriod(entry);
  if (period) {
    findings.push(period);
  }
  const doi = checkDoi(entry);
  if (doi) {
    findings.push(doi);
  }
  return findings;
}

/** First-author surname (or article-stripped title) used for ordering + duplicate keys. */
function sortKey(entry: string): string {
  const authorSeg = authorSegment(entry);
  let basis: string;
  if (authorSeg) {
    basis = authorSeg.includes(",")
      ? authorSeg.slice(0, authorSeg.indexOf(","))
      : (authorSeg.split(RE_WORD_SPLIT).pop() ?? authorSeg);
  } else {
    basis = entry.replace(RE_LEADING_ARTICLE, "");
  }
  return basis.toLowerCase().replace(RE_NON_LETTER, "");
}

/** Rule 6 — flag entries that break alphabetical (A–Z by surname) order. */
function checkOrdering(entries: string[]): Finding[] {
  const findings: Finding[] = [];
  const keys = entries.map(sortKey);
  for (let i = 1; i < keys.length; i++) {
    const prev = keys[i - 1];
    const cur = keys[i];
    if (prev && cur && prev.localeCompare(cur) > 0) {
      findings.push({
        rule: "list-ordering",
        title: `Entry ${i + 1} may be out of alphabetical order`,
        message: `MLA Works Cited lists are ordered A–Z by the first author's surname. Entry ${i + 1} sorts before entry ${i} ("${cur}" < "${prev}"). Check the ordering of these two entries.`,
        confidence: "high",
        learnMoreUrl: MLA_QUICK_GUIDE_URL,
        learnMoreLabel: "MLA: Works Cited order",
      });
    }
  }
  return findings;
}

/** Near-duplicate identity: surname + year (if any) + first significant title words. */
function duplicateKey(entry: string): string {
  const surname = sortKey(entry);
  const yearMatch = RE_YEAR_ANYWHERE.exec(entry);
  const year = yearMatch ? yearMatch[1] : "";
  const title = titleSegment(entry) ?? "";
  const titleWord = title
    .trim()
    .split(RE_WORD_SPLIT)
    .slice(0, 4)
    .join("")
    .toLowerCase()
    .replace(RE_PUNCT_STRIP, "");
  return `${surname}|${year}|${titleWord}`;
}

/** Rule 7 — flag near-duplicate entries (same surname + year + title start). */
function checkDuplicates(entries: string[]): Finding[] {
  const findings: Finding[] = [];
  const seen = new Map<string, number>();
  for (let i = 0; i < entries.length; i++) {
    const key = duplicateKey(entries[i]);
    if (!key || key.replace(RE_PIPES, "") === "") {
      continue;
    }
    const firstIndex = seen.get(key);
    if (firstIndex === undefined) {
      seen.set(key, i);
      continue;
    }
    findings.push({
      rule: "duplicate-entry",
      title: `Entry ${i + 1} may duplicate entry ${firstIndex + 1}`,
      message: `Entries ${firstIndex + 1} and ${i + 1} share the same author, year, and title start. A Works Cited list should list each source once.`,
      confidence: "high",
      learnMoreUrl: MLA_QUICK_GUIDE_URL,
      learnMoreLabel: "MLA: Works Cited list",
    });
  }
  return findings;
}

/**
 * Run the full deterministic MLA-9 Works Cited check over pasted text. Pure — no network,
 * no mutation, no dependency on the citation core.
 */
export function checkReferenceList(text: string): CheckReport {
  const rawEntries = splitEntries(text);
  const entries: EntryReport[] = rawEntries.map((entry, index) => ({
    position: index + 1,
    text: entry,
    findings: checkEntry(entry),
  }));
  const listFindings = [
    ...checkOrdering(rawEntries),
    ...checkDuplicates(rawEntries),
  ];
  const totalFindings =
    listFindings.length +
    entries.reduce((sum, e) => sum + e.findings.length, 0);
  return {
    entries,
    listFindings,
    totalEntries: entries.length,
    totalFindings,
  };
}
